import { createFileRoute } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
// Hero image URL imported via Vite's ?url so the build emits the hashed
// asset path. Used to inject a <link rel="preload" as="image"> hint below
// — the hero is the LCP element, and preloading lets the browser fetch
// it in parallel with HTML parse instead of waiting for CSS/JS discovery.
import heroImgUrl from "@/assets/hero-colorado.jpg?url";
import {
  Hero,
  IntentSelector,
  TrustBar,
  QuickAnswers,
  
  Services,
  Pricing,
  Results,
  Process,
  MeetTheOwner,
  // Education, // disabled — using /learn page for articles + videos instead
  Industries,
  ColoradoLocal,
  WhyUs,
  Portfolio,
  // Testimonials, // disabled — using StaticReviews (real Google reviews) instead
  FinalCTA,
} from "@/components/home/HomeSections";
import { StaticReviews } from "@/components/StaticReviews";
import { BoutiqueAdvantage } from "@/components/BoutiqueAdvantage";
import { GoogleMapsSection } from "@/components/home/GoogleMapsSection";
import { ColoradoSpringsMarketingSEO } from "@/components/home/ColoradoSpringsMarketingSEO";
import { ColoradoSpringsLandingBlock } from "@/components/home/ColoradoSpringsLandingBlock";
import { CLIENT_REVIEWS } from "@/lib/clientReviews";
import { buildLocalBusinessSchema } from "@/lib/entitySchema";

import {
  buildHomepageLocalBusinessAreaServed,
  buildHomepageWebSiteJsonLd,
} from "@/lib/serviceSchema";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      {
        title: "Colorado Springs Digital Marketing Agency | SEO & Web Design",
      },
      {
        name: "description",
        content:
          "Colorado Springs digital marketing agency — SEO, web design, Google Maps & PPC. Owner-led since 2006. 200+ Colorado businesses ranked. Free strategy call.",
      },
      { property: "og:title", content: "Colorado Springs Digital Marketing Agency | SEO & Web Design" },
      {
        property: "og:description",
        content:
          "Colorado Springs digital marketing agency — SEO, web design, Google Maps & PPC. Owner-led since 2006. 200+ Colorado businesses ranked. Free strategy call.",
      },
      { property: "og:url", content: "https://coloradowebimpressions.com/" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: "https://coloradowebimpressions.com/" },
      // LCP optimization: preload the hero image at highest priority so the
      // browser starts the fetch immediately on HTML parse, before CSS or
      // JS modules import it. Typically saves 300–600ms on LCP.
      {
        rel: "preload",
        as: "image",
        href: heroImgUrl,
        fetchPriority: "high",
      },
    ],
    scripts: [
      // Homepage BreadcrumbList — single-item list naming the home page.
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "@id": "https://coloradowebimpressions.com/#breadcrumbs",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://coloradowebimpressions.com/" },
          ],
        }),
      },
      // Homepage-only LocalBusiness enrichment: re-asserts the canonical
      // @id from __root.tsx and adds explicit per-city areaServed + a
      // statewide GeoCircle. Tells Google "we serve all of Colorado, and
      // specifically these 8 cities, from this HQ point."
      {
        type: "application/ld+json",
        children: JSON.stringify(buildHomepageLocalBusinessAreaServed()),
      },
      // WebSite node enables the sitelinks search box and links the site
      // root to the canonical Organization entity.
      {
        type: "application/ld+json",
        children: JSON.stringify(buildHomepageWebSiteJsonLd()),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "VideoObject",
          name: "About Colorado Web Impressions — Why We're Different",
          description:
            "A short introduction to Colorado Web Impressions from owner Chris Heidlebaugh — what makes our family-owned, in-house Colorado marketing agency different.",
          thumbnailUrl: "https://i.ytimg.com/vi/t3no84uGrms/maxresdefault.jpg",
          uploadDate: "2024-01-01",
          embedUrl: "https://www.youtube.com/embed/t3no84uGrms",
          contentUrl: "https://www.youtube.com/watch?v=t3no84uGrms",
          publisher: {
            "@type": "Organization",
            name: "Colorado Web Impressions",
            logo: {
              "@type": "ImageObject",
              url: "https://coloradowebimpressions.com/logo.png",
            },
          },
        }),
      },
      // Google Maps section — focused LocalBusiness node enriching the
      // canonical Organization @id with hasMap, hours, geo, and the same
      // top-3 review highlights shown on screen.
      {
        type: "application/ld+json",
        children: JSON.stringify({
          ...buildLocalBusinessSchema(),
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "5",
            reviewCount: "150",
          },
          review: [...CLIENT_REVIEWS]
            .filter((r) => r.rating === 5)
            .sort((a, b) => b.datePublished.localeCompare(a.datePublished))
            .slice(0, 3)
            .map((r) => ({
              "@type": "Review",
              author: { "@type": "Person", name: r.author },
              datePublished: r.datePublished,
              reviewRating: {
                "@type": "Rating",
                ratingValue: r.rating,
                bestRating: 5,
              },
              reviewBody: r.body,
            })),
        }),
      },
      // Service catalog — explicitly names the core services + Colorado
      // Springs service area so "SEO company Colorado Springs" and
      // "web design Colorado Springs" queries map to a typed entity.
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "@id": "https://coloradowebimpressions.com/#services",
          name: "Colorado Web Impressions",
          url: "https://coloradowebimpressions.com",
          provider: { "@id": "https://coloradowebimpressions.com/#organization" },
          areaServed: [
            { "@type": "City", name: "Colorado Springs", containedInPlace: { "@type": "State", name: "Colorado" } },
            { "@type": "State", name: "Colorado" },
          ],
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Digital Marketing Services in Colorado Springs",
            itemListElement: [
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "SEO Company Colorado Springs",
                  serviceType: "Search Engine Optimization",
                  areaServed: { "@type": "City", name: "Colorado Springs" },
                  url: "https://coloradowebimpressions.com/colorado-springs-seo",
                },
              },
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "Web Design Colorado Springs",
                  serviceType: "Website Design",
                  areaServed: { "@type": "City", name: "Colorado Springs" },
                  url: "https://coloradowebimpressions.com/colorado-springs-web-design",
                },
              },
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "Google Maps Optimization Colorado Springs",
                  serviceType: "Google Business Profile Optimization",
                  areaServed: { "@type": "City", name: "Colorado Springs" },
                  url: "https://coloradowebimpressions.com/colorado-springs-google-maps",
                },
              },
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "PPC Management Colorado Springs",
                  serviceType: "Pay-Per-Click Advertising",
                  areaServed: { "@type": "City", name: "Colorado Springs" },
                  url: "https://coloradowebimpressions.com/colorado-springs-ppc",
                },
              },
            ],
          },
        }),
      },
      // FAQPage — targets "Colorado Springs digital marketing" question SERPs.
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": "https://coloradowebimpressions.com/#faq",
          mainEntity: [
            {
              "@type": "Question",
              name: "What does a Colorado Springs digital marketing agency do?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "A Colorado Springs digital marketing agency earns placement in the four places local buyers look: the Google Map Pack, organic search, AI answers (ChatGPT, Perplexity, Google AI Overviews), and paid ads — and builds the website that converts that traffic into booked calls. At Colorado Web Impressions, that includes SEO, web design, Google Maps optimization, PPC, and GEO/AI optimization, all managed in-house from our Colorado Springs office.",
              },
            },
            {
              "@type": "Question",
              name: "How much does digital marketing cost in Colorado Springs?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Websites start at $1,850 one-time, monthly marketing starts at $950, and website edits and repairs are $250/hour. Website builds include one year of hosting. Ad spend is separate. Compare all packages on our pricing page.",
              },
            },
            {
              "@type": "Question",
              name: "How long does Colorado Springs SEO take to work?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Most Colorado Springs SEO campaigns see Map Pack movement in 60–120 days and meaningful organic ranking lifts in 4–6 months. Lighter-competition neighborhoods (Falcon, Monument, Peyton) move faster; competitive Springs categories (HVAC, roofing, attorneys) take longer.",
              },
            },
            {
              "@type": "Question",
              name: "Do you only work with Colorado Springs businesses?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "We're based in Colorado Springs but work with businesses across all of Colorado — Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, Monument, Falcon, and the full Front Range. We're Colorado-only by design, so every client gets a team that knows the local market.",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: Index,
});

function Index() {
  return (
    <SiteLayout>
      {/* 1. Attention — hero (dark) */}
      <Hero />
      {/* 2. Pricing — surfaced directly under hero for fast qualification (light) */}
      <Pricing />
      {/* 3. Intent selector — pick your path */}
      <IntentSelector />
      {/* 3b. Reviews — light break between the two dark sections */}
      <StaticReviews count={3} />
      {/* 4. Quick wayfinding (muted) */}
      <QuickAnswers />
      {/* 5. Instant credibility (light) */}
      <TrustBar />
      {/* 6. Services / solution (muted) */}
      <Services />
      {/* 7. Why us — differentiation (light) */}
      <WhyUs />
      {/* 6b. (NoSalespeople removed — merged into MeetTheOwner) */}
      {/* 7. Process — how it works (muted) */}
      <Process />
      {/* 8. Results / proof (dark) */}
      <Results />
      {/* 9. Social proof handled by <StaticReviews /> below — generic Testimonials removed */}
      {/* 10. Industries — relevance (muted) */}
      <Industries />
      {/* 10b. Boutique advantage — counters "small team" + "too cheap" objections */}
      <BoutiqueAdvantage />

      {/* 12. Meet the owner — humanize (dark) */}
      <MeetTheOwner />
      {/* 13. Education section removed — see /learn for live articles + videos */}
      {/* 14. Static Google reviews — server-rendered for SEO + perf. */}
      <StaticReviews
        eyebrow="Live Google reviews"
        heading="What Colorado clients are saying — straight from Google"
        description="Verified reviews from our Google Business Profile. We hand-pick a few to show here — see the full live feed on our reviews page."
      />
      {/* 14a. Colorado Springs landing block — local credibility + CTA */}
      <ColoradoSpringsLandingBlock />
      {/* 14b. Google Maps section — embedded map + NAP + review highlights */}
      <GoogleMapsSection />
      {/* 14c. Colorado Springs marketing/digital marketing SEO content + internal links */}
      <ColoradoSpringsMarketingSEO />
      {/* 15. Final CTA — close (dark) */}
      <FinalCTA />
    </SiteLayout>
  );
}
